import { useEffect, useState, useCallback, useRef } from "react";
import {
  fetchAllListsApi,
  createListApi,
  renameListApi,
  deleteListApi,
  addItemApi,
  addBatchItemsApi,
  updateItemApi,
  markPurchasedApi,
  fetchSuggestionsApi,
  fetchRecentlyUsedApi,
  fetchHistoryApi,
  fetchWorkspaceHistoryApi,
} from "../services/shoppingListService";

export function useShoppingLists({ workspaceId, token }) {
  const [lists, setLists] = useState([]);
  const [historyItems, setHistoryItems] = useState([]);
  const [activeListId, setActiveListId] = useState(() => {
    const saved = localStorage.getItem("lastOpenedShoppingList");
    return saved ? parseInt(saved) : null;
  });
  const [recentlyUsed, setRecentlyUsed] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const activeList = lists.find((l) => l.id === activeListId);

  // Fetch lists and suggestions
  const refreshData = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const fetchedLists = await fetchAllListsApi({ workspaceId, token });
      setLists(fetchedLists);

      // Validate active ID
      if (activeListId && !fetchedLists.some(l => l.id === activeListId)) {
        setActiveListId(null);
      }

      // Fetch suggestions if a list is active
      if (activeListId) {
        const suggs = await fetchSuggestionsApi({ listId: activeListId, token });
        setSuggestions(suggs);
        const recent = await fetchRecentlyUsedApi({ listId: activeListId, token });
        setRecentlyUsed(recent);
      }

      // Fetch Status History (Workspace level)
      // Trying workspace history first, if fails we might need to iterate lists (but let's hope for workspace endpoint)
      try {
        const hist = await fetchWorkspaceHistoryApi({ workspaceId, token });
        setHistoryItems(hist);
      } catch (e) {
        // Fallback: Fetch history for each list individually
        console.warn("Global history fetch failed, falling back to per-list history.", e);
        try {
          if (fetchedLists && fetchedLists.length > 0) {
            const allHistoryPromises = fetchedLists.map(l =>
              fetchHistoryApi({ listId: l.id, token }).catch(() => [])
            );
            const results = await Promise.all(allHistoryPromises);
            const combinedHistory = results.flat();
            setHistoryItems(combinedHistory);
          }
        } catch (err2) {
          console.error("Failed to fetch history fallback", err2);
        }
      }

    } catch (err) {
      setError("Failed to sync with server.");
    } finally {
      setLoading(false);
    }
  }, [workspaceId, token, activeListId]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const addItem = useCallback(async (itemData) => {
    if (!activeListId) return;

    // OPTIMISTIC UPDATE: If it's from suggestions, hide it immediately
    setSuggestions(prev => prev.filter(s => s.name.toLowerCase() !== itemData.name.toLowerCase()));
    // OPTIMISTIC UPDATE: If it's from recentelyUsed, hide it immediately
    setRecentlyUsed(prev => prev.filter(s => s.name.toLowerCase() !== itemData.name.toLowerCase()));

    try {
      const newItem = await addItemApi({
        listId: activeListId,
        itemData,
        token
      });

      setLists(prev => prev.map(l =>
        l.id === activeListId ? { ...l, items: [...(l.items || []), newItem] } : l
      ));
      setError(""); // Clear previous errors
      return newItem;
    } catch (err) {
      // Handle the 409 Conflict we added to the backend
      if (err.response?.status === 409) {
        setError("Dieser Artikel ist bereits auf der Liste.");
      } else {
        setError("Fehler beim Hinzufügen.");
      }
      // Rollback suggestions if needed
      refreshData();
    }
  }, [activeListId, token, refreshData]);

  const addBatchItems = useCallback(async (listId, items) => {
    try {
      if (!listId) return;
      await addBatchItemsApi({ listId, items, token });
      await refreshData();
    } catch (err) {
      setError("Fehler beim Hinzufügen der Zutaten.");
    }
  }, [token, refreshData]);

  const markPurchased = useCallback(async (itemId) => {
    if (!activeListId) return;

    // OPTIMISTIC UPDATE: Remove item immediately from the list to prevent double clicks and improve responsiveness
    setLists(prev => prev.map(l =>
      l.id === activeListId
        ? { ...l, items: (l.items || []).filter(i => i.id !== itemId) }
        : l
    ));

    try {
      await markPurchasedApi({ listId: activeListId, itemId, token });
      // Re-fetch to get updated suggestions and list state
      refreshData();
    } catch (err) {
      // If the item is already gone (404), we consider it a success state (idempotent behavior from UI perspective)
      if (err.status === 404) {
        refreshData();
        return;
      }
      console.error(err);
      setError("Error purchasing item");
      refreshData(); // Re-sync to restore item if it was a real error
    }
  }, [activeListId, token, refreshData]);

  // 3. Helper to update both state and storage
  const selectList = useCallback((id) => {
    setActiveListId(id);
    if (id) {
      localStorage.setItem("lastOpenedShoppingList", id);
    } else {
      localStorage.removeItem("lastOpenedShoppingList");
    }
  }, []);

  // Fix the return for createList to avoid double-calling
  const createList = useCallback(async (name) => {
    try {
      const newList = await createListApi({ name, workspaceId, token });
      await refreshData(); // Refresh to get full DTO
      selectList(newList.id);
      return newList;
    } catch (err) { setError("Error creating list"); }
  }, [workspaceId, token, selectList, refreshData]);

  const renameList = useCallback(async (listId, name) => {
    try {
      await renameListApi({ listId, name, token });
      setLists(prev => prev.map(l => l.id === listId ? { ...l, name } : l));
    } catch (err) {
      setError("Fehler beim Umbenennen der Liste");
    }
  }, [token]);

  const updateItem = useCallback(async (itemId, itemData) => {
    if (!activeListId) return;
    try {
      const updatedItem = await updateItemApi({
        listId: activeListId,
        itemId,
        itemData,
        token
      });

      // Update local state immediately
      setLists(prev => prev.map(l =>
        l.id === activeListId
          ? { ...l, items: l.items.map(i => i.id === itemId ? updatedItem : i) }
          : l
      ));
    } catch (err) {
      setError("Fehler beim Aktualisieren des Artikels");
    }
  }, [activeListId, token]);

  const deleteList = useCallback(async (listId) => {
    try {
      await deleteListApi({ listId, token });
      setLists((prev) => prev.filter((l) => l.id !== listId));
      if (activeListId === listId) selectList(null);
    } catch (err) { setError("Error deleting list"); }
  }, [token, activeListId, selectList]);

  return {
    lists, activeList, activeListId, setActiveListId: selectList,
    suggestions, recentlyUsed, historyItems, loading, addItem, updateItem, markPurchased,
    deleteList, renameList, createList, error, setError, addBatchItems
  };
}